// @flow

import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';

import apiService from 'src/services/api-service';
import {
  GET_STATUS_MIGRATE_DATA_NEW_SEARCH_REQUEST,
  GET_STATUS_MIGRATE_DATA_NEW_SEARCH_SUCCESS,
  GET_STATUS_MIGRATE_DATA_NEW_SEARCH_FAILURE,
  ENABLE_NEW_ONBOARDING_REQUEST,
  ENABLE_NEW_ONBOARDING_SUCCESS,
  ENABLE_NEW_ONBOARDING_FAILURE,
  MIGRATE_DATA_TO_NEW_SEARCH_V2_REQUEST,
  MIGRATE_DATA_TO_NEW_SEARCH_V2_SUCCESS,
  MIGRATE_DATA_TO_NEW_SEARCH_V2_FAILURE,
  SEND_SURVEY_FEEDBACK_REQUEST,
  SEND_SURVEY_FEEDBACK_SUCCESS,
  SEND_SURVEY_FEEDBACK_FAILURE,
  RESET_SURVEY_FEEDBACK,
  SEND_FEEDBACK_AFTER_RATING_5STARS_REQUEST,
  SEND_FEEDBACK_AFTER_RATING_5STARS_SUCCESS,
  SEND_FEEDBACK_AFTER_RATING_5STARS_FAILURE,
  UPDATE_FIELD,
  UPDATE_MULTI_FIELDS,
  MARK_DONE_TOUR_AB_TESTING_REQUEST,
  MARK_DONE_TOUR_AB_TESTING_SUCCESS,
  MARK_DONE_TOUR_AB_TESTING_FAILURE
} from 'stores/flow-type/common.type';

/**
 * migrate new feature
 * @return {object} The RSAA object for API middleware
 */
export const migrateNewFeatureV2 = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.migrate-data.migrate-new-search', {}, [
    MIGRATE_DATA_TO_NEW_SEARCH_V2_REQUEST,
    {
      type: MIGRATE_DATA_TO_NEW_SEARCH_V2_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: MIGRATE_DATA_TO_NEW_SEARCH_V2_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Common.ActionsNotify.migrateFailure'),
          i18n
        )
    }
  ]);
};

/**
 * Get status migrate to new search
 * @return {object} The RSAA object for API middleware
 */
export const getStatusMigrate = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.migrate-data.get-status', {}, [
    GET_STATUS_MIGRATE_DATA_NEW_SEARCH_REQUEST,
    {
      type: GET_STATUS_MIGRATE_DATA_NEW_SEARCH_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_STATUS_MIGRATE_DATA_NEW_SEARCH_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Common.ActionsNotify.getStatusMigrate'),
          i18n
        );
        return errorResult;
      }
    }
  ]);
};

/**
 * Enable new feature onboarding 042023
 * @return {object} The RSAA object for API middleware
 */
export const enableNewFeatureOnboarding = (
  enable: boolean,
  source: string = '',
  action: string = '',
  i18n: I18n
) => {
  return apiService.sendRequest(
    'admin-v3.onboarding.enable-onboarding-new-feature',
    {
      body: {
        enable,
        source,
        action
      }
    },
    [
      ENABLE_NEW_ONBOARDING_REQUEST,
      {
        type: ENABLE_NEW_ONBOARDING_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: ENABLE_NEW_ONBOARDING_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Common.ActionsNotify.getStatusMigrate'),
            i18n
          );
          return errorResult;
        }
      }
    ]
  );
};

/**
 * Submit survey feedback
 * @return {object} The RSAA object for API middleware
 */
export const sendSurveyFeedback = (body: Object, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.feedback.survey-feedback', { body }, [
    SEND_SURVEY_FEEDBACK_REQUEST,
    {
      type: SEND_SURVEY_FEEDBACK_SUCCESS,
      payload: async (action, state, res) => {
        const result = await processRes(
          res,
          'custom',
          i18n.translate('AdminHTML.Common.SupportTicketActions.sendSupportTicket_Success'),
          i18n
        );
        return { ...result, rating: body.rating };
      }
    },
    {
      type: SEND_SURVEY_FEEDBACK_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'custom',
          i18n.translate('AdminHTML.Common.SupportTicketActions.sendSupportTicket_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * Submit feedback after customer rating 5 stars
 * @return {object} The RSAA object for API middleware
 */
export const sendFeedbackAfterCustomerRating5Sstars = (
  isSubmit: boolean,
  body: Object,
  i18n: I18n
) => {
  return apiService.sendRequest(
    'admin-v3.feedback.feedback-after-customer-rating-5stars',
    { body },
    [
      SEND_FEEDBACK_AFTER_RATING_5STARS_REQUEST,
      {
        type: SEND_FEEDBACK_AFTER_RATING_5STARS_SUCCESS,
        payload: async (action, state, res) => {
          const result = await processRes(
            res,
            'custom',
            i18n.translate('AdminHTML.Common.FeedbackAfterRating5Stars.SubmitForm_Success'),
            i18n
          );
          return { ...result, isSubmit };
        }
      },
      {
        type: SEND_FEEDBACK_AFTER_RATING_5STARS_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'custom',
            i18n.translate('AdminHTML.Common.FeedbackAfterRating5Stars.SubmitForm_Failure'),
            i18n
          )
      }
    ]
  );
};

/**
 * Reset survey feedback state
 * @return {object} The RSAA object for API middleware
 */
export const resetSurveyFeedback = () => {
  return {
    type: RESET_SURVEY_FEEDBACK,
    payload: {}
  };
};

/**
 * Call to update field in reducer
 * @returns An object with a type and a payload.
 */
export const handleUpdateField = (fieldName: string, data: any) => {
  return { type: UPDATE_FIELD, payload: { fieldName, data } };
};

/**
 * Call to update multi fields in reducer
 * @returns An object with a type and a payload.
 */
export const handleUpdateMultiFields = (data: any) => {
  return { type: UPDATE_MULTI_FIELDS, payload: { data } };
};

/**
 * Call to update skip tour guide ab-testing
 * @returns An object with a type and a payload.
 */
export const handleSkipTourABTestingGuide = (meta: any, i18n) => {
  return apiService.sendRequest(
    'admin-v3.ab-testing.mark-done-tour',
    {
      body: {
        value: true
      }
    },
    [
      MARK_DONE_TOUR_AB_TESTING_REQUEST,
      {
        type: MARK_DONE_TOUR_AB_TESTING_SUCCESS,
        payload: async (action, state, res) => {
          return await getJSON(res);
        },
        meta
      },
      {
        type: MARK_DONE_TOUR_AB_TESTING_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ABTesting.Actions.markDoneTour_Failure'),
            i18n
          );
          return errorResult;
        }
      }
    ]
  );
};
